/**
 * 700Credit XML Gateway Integration Service
 * Supports soft pulls (PREQUALIFY) and hard pulls (CREDIT)
 * Based on 700Credit XML Interface Specification Ver 6.89
 */

import { supabase } from '../lib/supabase';

class CreditBureauService {
  constructor() {
    // Product-specific accounts from 700Credit
    this.creditAccount = import.meta.env?.VITE_700CREDIT_ACCOUNT || 'Paradise';
    this.creditPassword = import.meta.env?.VITE_700CREDIT_PASSWORD || 'Test1234567!';
    this.pqAccount = import.meta.env?.VITE_700CREDIT_PQ_ACCOUNT || 'ParadisePQ';
    this.pqPassword = import.meta.env?.VITE_700CREDIT_PQ_PASSWORD || 'Test1234567!';
    this.scanAccount = import.meta.env?.VITE_700CREDIT_SCAN_ACCOUNT || 'ParadiseScan';
    this.scanPassword = import.meta.env?.VITE_700CREDIT_SCAN_PASSWORD || 'Test1234567!';
    this.sandboxMode = import.meta.env?.VITE_700CREDIT_SANDBOX === 'true';
    
    // API Endpoints
    this.testEndpoint = 'https://gateway.700creditsolution.com/Request';
    this.productionEndpoint = 'https://gateway.700dealer.com/Request';
  }

  getCredentials(product) {
    switch (product) {
      case 'PREQUALIFY':
      case 'PRESCREEN':
        return { account: this.pqAccount, password: this.pqPassword };
      case 'QUICKSCAN':
        return { account: this.scanAccount, password: this.scanPassword };
      default:
        return { account: this.creditAccount, password: this.creditPassword };
    }
  }

  get endpoint() {
    return this.sandboxMode ? this.testEndpoint : this.productionEndpoint;
  }

  /**
   * Build form data for 700Credit API request
   */
  buildFormData(params) {
    const formData = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        formData.append(key, value);
      }
    });
    return formData;
  }

  /**
   * Parse XML response from 700Credit
   */
  parseXMLResponse(xmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');
    
    const getTextContent = (tagName) => {
      const element = doc.getElementsByTagName(tagName)[0];
      return element ? element.textContent : null;
    };

    const getAllElements = (tagName) => {
      return Array.from(doc.getElementsByTagName(tagName));
    };

    // Check for errors first
    const errorElements = getAllElements('Creditsystem_Error');
    if (errorElements.length > 0) {
      const errors = errorElements.map(el => ({
        id: el.getAttribute('id'),
        message: el.getAttribute('message') || el.textContent
      }));
      return { success: false, errors };
    }

    // Parse successful response
    const result = {
      success: true,
      transId: getTextContent('Transid'),
      token: getTextContent('Token'),
      xmlVersion: getTextContent('XML_Version'),
      
      // Score data
      score: getTextContent('Score'),
      scoreRange: getTextContent('ScoreRange'),
      scorecardName: getTextContent('ScorecardName'),
      tier: getTextContent('Tier'),
      
      // Result codes
      resultCode: getTextContent('ResultCode'),
      resultDescription: getTextContent('ResultDescription'),
      
      // Duplicate check
      duplicateFound: getTextContent('DuplicateFound') === 'Y',
      duplicateMessage: getTextContent('DuplicateMessage'),
      
      // OFAC results
      ofacResult: getTextContent('Results'),
      
      // Score factors
      scoreFactors: [],
      
      // iFrame URL for viewing report
      iframeUrl: null,
      
      // Auto summary
      autoSummary: {
        totalBalance: getTextContent('Total_Balance'),
        totalMonthlyPay: getTextContent('Total_Monthly_Pay'),
        totalAutoTrades: getTextContent('Total_Auto_Trades'),
        totalOpenAutoTrades: getTextContent('Total_Open_Auto_Trades')
      },
      
      // Credit summary
      creditSummary: {
        totalTrades: getTextContent('Total_Trades'),
        oldestTrade: getTextContent('Oldest_Trade'),
        totalCurrentTrades: getTextContent('Total_Current_Trades'),
        totalNegativeTrades: getTextContent('Total_Negative_Trades'),
        totalCollections: getTextContent('Total_Collections'),
        totalPublicRecords: getTextContent('Total_Public_Records'),
        totalInquiries: getTextContent('Total_Inquiries'),
        totalInquiries6mos: getTextContent('Total_Inquiries_6mos')
      },

      // Raw XML for storage
      rawXml: xmlString
    };

    // Extract score factors
    for (let i = 1; i <= 5; i++) {
      const factor = getTextContent(`factor${i}`);
      if (factor) {
        result.scoreFactors.push(factor);
      }
    }

    // Extract iFrame URL from custom_report CDATA
    const customReport = getTextContent('custom_report');
    if (customReport) {
      const iframeMatch = customReport.match(/SRC="([^"]+)"/i);
      if (iframeMatch) {
        result.iframeUrl = iframeMatch[1].replace(/&amp;/g, '&');
      }
    }

    return result;
  }

  /**
   * Soft Pull - PreQualify (No impact on credit score)
   * Use for initial customer qualification
   */
  async softPull(applicant, options = {}) {
    const bureau = options.bureau || 'TU'; // Default to TransUnion for soft pulls
    const creds = this.getCredentials('PREQUALIFY');
    
    const params = {
      ACCOUNT: creds.account,
      PASSWD: creds.password,
      PRODUCT: 'PREQUALIFY',
      BUREAU: bureau,
      PASS: '2',
      PROCESS: 'PCCREDIT',
      
      // Applicant info
      NAME: `${applicant.firstName} ${applicant.lastName}`,
      SSN: applicant.ssn?.replace(/-/g, '') || '',
      DOB: this.formatDate(applicant.dob),
      ADDRESS: applicant.address,
      CITY: applicant.city,
      STATE: applicant.state,
      ZIP: applicant.zip?.substring(0, 5),
      
      // Optional fields
      EMAIL: applicant.email || '',
      PHONE: applicant.phone || '',
      MOBILE: applicant.mobile || ''
    };

    // Add employment info if provided
    if (applicant.employer) {
      params.EMPLOYER = applicant.employer;
      params.MINCOME = applicant.monthlyIncome || '';
    }

    return this.makeRequest(params, 'soft', applicant);
  }

  /**
   * Hard Pull - Full Credit Report (Impacts credit score)
   * Use when customer is ready to apply for financing
   */
  async hardPull(applicant, options = {}) {
    const bureaus = options.bureaus || ['TU', 'EFX', 'XPN']; // All three bureaus
    const isMultiBureau = bureaus.length > 1;
    const creds = this.getCredentials('CREDIT');
    
    const params = {
      ACCOUNT: creds.account,
      PASSWD: creds.password,
      PRODUCT: 'CREDIT',
      PASS: '2',
      PROCESS: 'PCCREDIT',
      
      // Applicant info (required)
      NAME: `${applicant.firstName} ${applicant.lastName}`,
      SSN: applicant.ssn?.replace(/-/g, ''),
      DOB: this.formatDate(applicant.dob),
      ADDRESS: applicant.address,
      CITY: applicant.city,
      STATE: applicant.state,
      ZIP: applicant.zip?.substring(0, 5),
      
      // Optional fields
      EMAIL: applicant.email || '',
      PHONE: applicant.phone || '',
      MOBILE: applicant.mobile || ''
    };

    // Single vs multi-bureau
    if (isMultiBureau) {
      params.MULTIBUR = bureaus.join(':');
    } else {
      params.BUREAU = bureaus[0];
    }

    // Bypass duplicate check if requested
    if (options.bypassDuplicate) {
      params.APP_MODIFIED = 'Y';
    }

    // Add co-applicant if provided
    if (applicant.coApplicant) {
      const co = applicant.coApplicant;
      params.SPOUSE = `${co.firstName} ${co.lastName}`;
      params.SPOUSESSN = co.ssn?.replace(/-/g, '');
      params.SPOUSEDOB = this.formatDate(co.dob);
      params.SPOUSEADDRESS = co.address || applicant.address;
      params.SPOUSECITY = co.city || applicant.city;
      params.SPOUSESTATE = co.state || applicant.state;
      params.SPOUSEZIP = co.zip?.substring(0, 5) || applicant.zip?.substring(0, 5);
    }

    // Add employment info
    if (applicant.employer) {
      params.EMPLOYER = applicant.employer;
      params.POSITION = applicant.position || '';
      params.YEARS = applicant.employmentYears || '';
      params.MINCOME = applicant.monthlyIncome || '';
      params.WPHONE = applicant.workPhone || '';
    }

    // Add housing info
    if (applicant.housingType) {
      params.HOUSING = applicant.housingType; // Rent, Own, OwnFreeAndClear, Other
      params.HOUSINGPAY = applicant.housingPayment || '';
    }

    // Add vehicle info if available
    if (options.vehicle) {
      const v = options.vehicle;
      params.ISNEW = v.isNew ? 'True' : 'False';
      params.YEAR = v.year || '';
      params.NMAKE = v.make || '';
      params.MODEL = v.model || '';
      params.NVINNUMBER = v.vin || '';
      params.MILEAGE = v.mileage || '';
    }

    // Add finance info
    if (options.finance) {
      const f = options.finance;
      params.SELLINGPRICE = f.sellingPrice || '';
      params.DOWNPAYMENT = f.downPayment || '';
      params.REQAMOUNT = f.requestedAmount || '';
      params.TERMS = f.terms || '';
    }

    return this.makeRequest(params, 'hard', applicant);
  }

  /**
   * Make the actual API request to 700Credit via Supabase Edge Function
   * This avoids CORS issues by proxying through our backend
   */
  async makeRequest(params, productType, applicant, options = {}) {
    console.log(`🔒 Making ${productType} request via Supabase Edge Function...`);
    
    try {
      // Get Supabase URL for edge function
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Supabase configuration missing');
      }

      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/credit-pull`;
      
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          applicant,
          product: params.PRODUCT,
          bureau: params.BUREAU,
          bureaus: params.MULTIBUR ? params.MULTIBUR.split(':') : undefined,
          businessName: options.businessName,
          bypassDuplicate: options.bypassDuplicate
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(`700Credit Error: ${result.error || 'Unknown error'}`);
      }

      console.log('📄 Received response from 700Credit via Edge Function');

      // Enhance result with calculated fields
      const enhancedResult = {
        ...result,
        pullType: productType,
        product: params.PRODUCT,
        bureau: params.BUREAU || params.MULTIBUR,
        timestamp: new Date().toISOString(),
        applicantName: applicant ? `${applicant.firstName} ${applicant.lastName}` : options.businessName || '',
        decision: this.calculateDecision(parseInt(result.score) || 0),
        recommendations: this.generateRecommendations(parseInt(result.score) || 0)
      };

      // Log to database for compliance
      await this.logCreditPull(enhancedResult, applicant);

      return enhancedResult;

    } catch (error) {
      console.error('❌ 700Credit API Error:', error);
      throw error;
    }
  }

  /**
   * Retrieve a previously pulled report using TransID or Token
   */
  async retrieveReport(reference, type = 'transId') {
    const creds = this.getCredentials('CREDIT');
    const params = {
      ACCOUNT: creds.account,
      PASSWD: creds.password,
      PASS: type === 'transId' ? '109' : '2',
      PROCESS: 'PCCREDIT',
      REF: reference
    };

    const formData = this.buildFormData(params);
    
    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: formData.toString()
    });

    const xmlText = await response.text();
    return this.parseXMLResponse(xmlText);
  }

  /**
   * Save application data without pulling credit (SAVEONLY)
   */
  async saveOnly(applicant, losSystem = null) {
    const creds = this.getCredentials('CREDIT');
    const params = {
      ACCOUNT: creds.account,
      PASSWD: creds.password,
      PRODUCT: 'SAVEONLY',
      PASS: '2',
      PROCESS: 'PCCREDIT',
      
      NAME: `${applicant.firstName} ${applicant.lastName}`,
      ADDRESS: applicant.address,
      CITY: applicant.city,
      STATE: applicant.state,
      ZIP: applicant.zip?.substring(0, 5),
      SSN: applicant.ssn?.replace(/-/g, '') || '',
      EMAIL: applicant.email || '',
      PHONE: applicant.phone || ''
    };

    if (losSystem) {
      params.LOS_SYSTEM = losSystem; // R1, DT, CUDL, APPONE
    }

    return this.makeRequest(params, 'saveonly', applicant);
  }

  /**
   * OFAC Check (Office of Foreign Assets Control)
   */
  async ofacCheck(applicant) {
    const creds = this.getCredentials('OFAC');
    const params = {
      ACCOUNT: creds.account,
      PASSWD: creds.password,
      PRODUCT: 'OFAC',
      PASS: '2',
      PROCESS: 'PCCREDIT',
      NAME: `${applicant.firstName} ${applicant.lastName}`
    };

    return this.makeRequest(params, 'ofac', applicant);
  }

  /**
   * Red Flag Identity Check
   * Required: First Name, Last Name, Address, City, State, Zip
   * Optional: SSN
   */
  async redFlagCheck(applicant) {
    const creds = this.getCredentials('REDFLAG');
    const params = {
      ACCOUNT: creds.account,
      PASSWD: creds.password,
      PRODUCT: 'REDFLAG',
      PASS: '2',
      PROCESS: 'PCCREDIT',
      NAME: `${applicant.firstName} ${applicant.lastName}`,
      ADDRESS: applicant.address,
      CITY: applicant.city,
      STATE: applicant.state,
      ZIP: applicant.zip?.substring(0, 5),
      SSN: applicant.ssn?.replace(/-/g, '') || ''
    };

    return this.makeRequest(params, 'redflag', applicant);
  }

  /**
   * ID Check - Identity verification using SSN only
   * Required: SSN only (all other parameters ignored)
   */
  async idCheck(applicant) {
    const creds = this.getCredentials('IDCHECK');
    const params = {
      ACCOUNT: creds.account,
      PASSWD: creds.password,
      PRODUCT: 'IDCHECK',
      PASS: '2',
      PROCESS: 'PCCREDIT',
      SSN: applicant.ssn?.replace(/-/g, '')
    };

    return this.makeRequest(params, 'idcheck', applicant);
  }

  /**
   * OFAC Business Search
   * Required: NAME field only (business name)
   */
  async ofacBusinessCheck(businessName) {
    const creds = this.getCredentials('OFACB');
    const params = {
      ACCOUNT: creds.account,
      PASSWD: creds.password,
      PRODUCT: 'OFACB',
      PASS: '2',
      PROCESS: 'PCCREDIT',
      NAME: businessName
    };

    return this.makeRequest(params, 'ofacb', { firstName: businessName, lastName: '' });
  }

  /**
   * Prescreen - Soft inquiry for marketing/prequalification
   * Required: First Name, Last Name, Address, City, State, Zip
   * No SSN required
   */
  async prescreen(applicant) {
    const creds = this.getCredentials('PRESCREEN');
    const params = {
      ACCOUNT: creds.account,
      PASSWD: creds.password,
      PRODUCT: 'PRESCREEN',
      PASS: '2',
      PROCESS: 'PCCREDIT',
      NAME: `${applicant.firstName} ${applicant.lastName}`,
      ADDRESS: applicant.address,
      CITY: applicant.city,
      STATE: applicant.state,
      ZIP: applicant.zip?.substring(0, 5)
    };

    return this.makeRequest(params, 'prescreen', applicant);
  }

  // ==========================================
  // HELPER METHODS
  // ==========================================

  formatDate(dateString) {
    if (!dateString) return '';
    
    // Handle various date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  }

  calculateDecision(score) {
    if (!score || score === 0) return 'UNABLE TO SCORE';
    if (score >= 720) return 'TIER 1 - Excellent';
    if (score >= 680) return 'TIER 2 - Good';
    if (score >= 640) return 'TIER 3 - Fair';
    if (score >= 600) return 'TIER 4 - Subprime';
    if (score >= 550) return 'TIER 5 - Deep Subprime';
    return 'TIER 6 - High Risk';
  }

  generateRecommendations(score) {
    const recommendations = [];
    
    if (!score || score === 0) {
      recommendations.push('Unable to determine score - verify applicant information');
      return recommendations;
    }

    if (score >= 720) {
      recommendations.push('Qualifies for best rates and terms');
      recommendations.push('Consider 0% manufacturer financing if available');
      recommendations.push('May qualify for minimal down payment');
    } else if (score >= 680) {
      recommendations.push('Good financing options available');
      recommendations.push('10% down payment recommended');
      recommendations.push('Shop multiple lenders for best rate');
    } else if (score >= 640) {
      recommendations.push('Standard subprime rates apply');
      recommendations.push('15-20% down payment recommended');
      recommendations.push('Consider shorter loan term');
    } else if (score >= 600) {
      recommendations.push('Limited lender options');
      recommendations.push('20%+ down payment required');
      recommendations.push('Proof of income essential');
      recommendations.push('Consider co-signer');
    } else {
      recommendations.push('High-risk financing only');
      recommendations.push('Minimum 25% down payment');
      recommendations.push('Co-signer strongly recommended');
      recommendations.push('Buy-here-pay-here may be option');
    }

    return recommendations;
  }

  estimateAPR(score) {
    if (score >= 750) return 4.99;
    if (score >= 700) return 6.99;
    if (score >= 650) return 9.99;
    if (score >= 600) return 14.99;
    if (score >= 550) return 19.99;
    return 24.99;
  }

  // ==========================================
  // DATABASE LOGGING
  // ==========================================

  async logCreditPull(result, applicant) {
    try {
      const logEntry = {
        trans_id: result.transId,
        token: result.token,
        pull_type: result.pullType,
        bureau: result.bureau,
        applicant_name: result.applicantName,
        score: parseInt(result.score) || null,
        score_range: result.scoreRange,
        tier: result.tier,
        decision: result.decision,
        result_code: result.resultCode,
        result_description: result.resultDescription,
        ofac_result: result.ofacResult,
        duplicate_found: result.duplicateFound,
        iframe_url: result.iframeUrl,
        score_factors: result.scoreFactors,
        auto_summary: result.autoSummary,
        credit_summary: result.creditSummary,
        recommendations: result.recommendations,
        raw_response: result.rawXml,
        created_at: new Date().toISOString(),
        environment: this.sandboxMode ? 'sandbox' : 'production'
      };

      const { error } = await supabase
        .from('credit_reports')
        .insert(logEntry);

      if (error) {
        console.warn('Failed to log credit pull to database:', error);
      } else {
        console.log('✅ Credit pull logged to database');
      }
    } catch (err) {
      console.warn('Error logging credit pull:', err);
    }
  }

  async getRecentPulls(applicantName, days = 30) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const { data, error } = await supabase
      .from('credit_reports')
      .select('*')
      .eq('applicant_name', applicantName)
      .gte('created_at', cutoffDate.toISOString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching recent pulls:', error);
      return [];
    }

    return data;
  }

  async getCreditReportById(transId) {
    const { data, error } = await supabase
      .from('credit_reports')
      .select('*')
      .eq('trans_id', transId)
      .single();

    if (error) {
      console.error('Error fetching credit report:', error);
      return null;
    }

    return data;
  }
}

// Export singleton instance
export const creditBureauService = new CreditBureauService();
export { CreditBureauService };
